import Badge from '@/common/components/Badge'
import Box from '@/common/components/Box'
import GeoSelectOption from '@/common/components/GeoSelectOption'
import ResumeItem from '@/common/components/ResumeItem'
import { formatCurrency, formatDecimal, formatUnit } from '@/common/utils/formatters'
import { options as priceRuleOptions } from '@/features/publicCompanyLeadsFunnel/constants/priceRuleOptions.constant'
import { ContractProductCategoryProductData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { FileBoxIcon } from 'lucide-react'

interface ContractProductCategoriesProductItemProps {
  productCategoryIndex: number
  index: number
  contractProductCategoryProduct: ContractProductCategoryProductData
}

export const ContractProductCategoriesProductItem = ({
  contractProductCategoryProduct,
  productCategoryIndex,
  index,
}: ContractProductCategoriesProductItemProps) => {
  return (
    <Box className="flex flex-col gap-4 p-3">
      <div className="border-s border-blue-800 px-3 py-2 flex">
        <Badge
          Icon={FileBoxIcon}
          iconProps={{ className: 'size-5', strokeWidth: 1 }}
          text={contractProductCategoryProduct?.product?.display_name}
          overwriteClassName
          valueDataTestid={`contract-product-category-${productCategoryIndex}-product-${index}-badge`}
        />
      </div>

      <div className="flex items-center">
        <div className="flex gap-6">
          <ResumeItem
            valueDataTestid={`contract-product-category-${productCategoryIndex}-product-${index}-value_in_currency`}
            label="Cota total em valor"
            value={
              contractProductCategoryProduct.global_value_in_currency
                ? formatCurrency(Number(contractProductCategoryProduct.global_value_in_currency))
                : ''
            }
            labelClassName="text-white-400"
            valueClassName="text-md"
          />

          <ResumeItem
            valueDataTestid={`contract-product-category-${productCategoryIndex}-product-${index}-value_in_unit_for_consumption`}
            label="Cota total em quantidade"
            value={
              contractProductCategoryProduct.global_value_in_unit_for_consumption
                ? formatUnit(
                  formatDecimal(Number(contractProductCategoryProduct.global_value_in_unit_for_consumption), 2),
                  contractProductCategoryProduct.product?.unit_for_quantity,
                )
                : ''
            }
            labelClassName="text-white-400"
            valueClassName="text-md"
          />
        </div>
      </div>

      <div className="flex gap-6">
        <ResumeItem
          valueDataTestid={`contract-product-category-${productCategoryIndex}-product-${index}-price_rule`}
          label="Regras de preço"
          value={
            contractProductCategoryProduct.price_rule
              ? priceRuleOptions?.find((option) => option.value === contractProductCategoryProduct.price_rule)?.label ||
              ''
              : ''
          }
          labelClassName="text-white-400"
          valueClassName="text-md"
        />

        {contractProductCategoryProduct.price_rule === 'fixed' &&
          !contractProductCategoryProduct?.contract_product_category_product_prices?.some((price) => price.geo) && (
            <ResumeItem
              valueDataTestid={`contract-product-category-${productCategoryIndex}-product-${index}-price_rule_value-0`}
              label="Preço fixo a ser praticado"
              value={
                contractProductCategoryProduct.contract_product_category_product_prices?.[0]?.value
                  ? formatCurrency(
                    Number(contractProductCategoryProduct.contract_product_category_product_prices?.[0]?.value),
                  )
                  : ''
              }
              className="text-white-400"
              valueClassName="text-md"
            />
          )}
      </div>

      {contractProductCategoryProduct.price_rule === 'fixed' &&
        !!contractProductCategoryProduct.contract_product_category_product_prices?.length &&
        !!contractProductCategoryProduct?.contract_product_category_product_prices?.some((price) => price.geo) && (
          <div className="flex flex-col gap-4">
            {contractProductCategoryProduct.contract_product_category_product_prices?.map((productPrice, ruleIndex) => (
              <Box key={productPrice.id} className="p-3">
                <div className="border-s border-blue-400 flex items-center gap-4 px-3">
                  {productPrice.geo && (
                    <ResumeItem
                      label="Região de atuação"
                      valueDataTestid={`contract-product-category-${productCategoryIndex}-product-${index}-price_rule_geo-${ruleIndex}`}
                      value={<GeoSelectOption geo={productPrice.geo} />}
                      labelClassName="text-white-400"
                      valueClassName="text-md"
                    />
                  )}

                  <ResumeItem
                    label="Valor a ser praticado"
                    valueDataTestid={`contract-product-category-${productCategoryIndex}-product-${index}-price_rule_value-${ruleIndex}`}
                    value={productPrice.value ? formatCurrency(Number(productPrice.value)) : ''}
                    labelClassName="text-white-400"
                    valueClassName="text-md"
                  />
                </div>
              </Box>
            ))}
          </div>
        )}
    </Box>
  )
}
